import { Link } from "react-router-dom";
import { ArrowRight, Building2, GraduationCap, Trophy, Shield, Layers, Cpu, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const SEGMENTS = [
  { id: "high_school",  icon: GraduationCap, tone: "#22D3EE", title: "High Schools",
    body: "Move beyond spreadsheets. Give every coach access to the same intelligence platform elite programs use." },
  { id: "college",      icon: Trophy,        tone: "#F59E0B", title: "Colleges & Universities",
    body: "Unify coaches, athletic trainers, S&C, and sports medicine into a single Athlete Intelligence Profile™." },
  { id: "athletic_dept",icon: Building2,     tone: "#A78BFA", title: "Athletic Departments",
    body: "Department-wide roster health, budget optimization, cross-sport benchmarking, executive intelligence." },
  { id: "pro",          icon: Shield,        tone: "#D90429", title: "Professional Organizations",
    body: "Standardize intelligence across every affiliate and developmental level. Front-office + medical + performance." },
  { id: "military",     icon: Target,        tone: "#10B981", title: "Military Human Performance",
    body: "Unit readiness, movement optimization, fatigue management, longitudinal performance tracking." },
  { id: "olympic",      icon: Layers,        tone: "#F97316", title: "Olympic Programs",
    body: "Federation-scale, cycles-long periodization with continuous biometric + workload intelligence." },
  { id: "facility",     icon: Cpu,           tone: "#EAB308", title: "Performance Facilities",
    body: "Multi-client management, testing workflows, auto-generated athlete reports, billing." },
];

export default function Enterprise() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="enterprise-page">
      <SiteNav />
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Enterprise</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          Organizational intelligence.<br/>
          <span className="text-white/50">Scalable across every sport, team, and site.</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl">
          BioHacker Sports™ is the enterprise platform that transforms disconnected performance data
          into cross-team decision-making. One profile. One cloud. One shared record.
        </p>
      </section>

      <section className="py-14 max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SEGMENTS.map((s) => (
          <div key={s.id} className="ink-card rounded-2xl p-6" data-testid={`enterprise-segment-${s.id}`}>
            <s.icon className="h-6 w-6 mb-3" style={{ color: s.tone }} />
            <div className="font-display font-black text-white text-lg">{s.title}</div>
            <div className="mt-2 text-sm text-white/65 leading-relaxed">{s.body}</div>
          </div>
        ))}
      </section>

      <section className="py-16 border-t border-white/8 text-center">
        <h2 className="font-display font-black text-3xl text-white tracking-tight">Ready for enterprise scale?</h2>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/request-demo" data-testid="enterprise-cta-demo">
            <Button size="lg" className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request enterprise demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/enterprise/contact" data-testid="enterprise-cta-contact">
            <Button size="lg" variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Talk to enterprise sales
            </Button>
          </Link>
          <Link to="/compliance" data-testid="enterprise-cta-compliance">
            <Button size="lg" variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Trust & compliance
            </Button>
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
